import {
  UpdateStripeBillingAddressRequest,
  schUpdateStripeBillingAddressRequest,
  schUpdateStripeBillingAddressResponse,
} from '@aloft/toolkit-shared';
import { Button } from '@aloft/toolkit-sui';
import { AddressElement, useElements } from '@stripe/react-stripe-js';

import { useUpdateStripeBillingAddress } from '../../../../hooks/api/toolkitComponents.ts';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { StripeAddressElementValue } from './types.ts';
import { useCheckoutSteps } from './useCheckoutSteps.ts';

type Props = {
  onComplete: (params: { address: StripeAddressElementValue }) => Promise<void>;
};

export const BillingAddressForm = ({ onComplete }: Props) => {
  const toast = useToaster();
  const elements = useElements();
  const { nextStep } = useCheckoutSteps();
  const updateBillingAddress = useUpdateStripeBillingAddress({
    onError: (error) => {
      console.error('Could not validate billing details.', JSON.stringify(error, null, 2));
      toast({
        id: 'update-billing-address-error',
        type: 'error',
        title: 'Could not validate billing details',
        body: [
          'Sorry, there was an error confirming your billing address.',
          'Please ensure your billing address is valid and entered properly, then try again.',
        ].join(' '),
      });
    },
  });

  // TODO: Extract network request logic to custom mutation hooks
  // this submit handler validates the address element input then updates the Customer billing address and
  // ensures it's valid for tax calculation: https://docs.stripe.com/elements/address-element/collect-addresses
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!elements) return;

    const addressElement = elements.getElement('address');
    if (!addressElement) return;

    // validate address input
    const { complete, value } = await addressElement.getValue();
    if (!complete) return;

    const addressBody: UpdateStripeBillingAddressRequest = {
      city: value.address.city,
      country: value.address.country,
      line1: value.address.line1,
      line2: value.address.line2 ?? undefined,
      postal_code: value.address.postal_code,
      state: value.address.state,
    };

    // update the Stripe customer's billing address
    const response = await updateBillingAddress.mutateAsync({
      body: schUpdateStripeBillingAddressRequest.parse(addressBody),
    });
    const { automaticTaxStatus } = schUpdateStripeBillingAddressResponse.parse(response);

    // handle invalid tax status
    if (automaticTaxStatus === 'failed' || automaticTaxStatus === 'unrecognized_location') {
      console.error(`Automatic tax status not supported: ${automaticTaxStatus}.`, addressBody);
      toast({
        id: 'tax-status-location-error',
        type: 'error',
        title: 'Could not validate billing details',
        body: [
          'Sorry, there was an error confirming your billing address.',
          'Please ensure your billing address is valid and entered properly, then try again.',
        ].join(' '),
      });
      return;
    }

    await onComplete({
      address: value,
    });
    nextStep();
  };

  const isLoading = updateBillingAddress.isPending;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <AddressElement options={{ mode: 'billing' }} />
      <Button
        type="submit"
        text="Next: Payment details"
        onClick={() => ({})}
        isLoading={isLoading}
        disabled={isLoading}
      />
    </form>
  );
};
